import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AppPermissionsService } from '../services/permissions/permissions.service';

/**
 * Structural directive that conditionally renders content based on permissions.
 *
 * Usage:
 *   <button *authorityLabsHasPermission="'orders.create'">Create Order</button>
 *   <button *authorityLabsHasPermission="['orders.edit', 'orders.create']">Edit</button>
 *
 * When an array is provided, the element is shown if the user has ANY of the permissions.
 */
@Directive({
  selector: '[authorityLabsHasPermission]'
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private requiredPermissions: string[] = [];
  private hasView = false;
  private elseTemplateRef: TemplateRef<any> | null = null;
  private subscription: Subscription;

  @Input()
  set authorityLabsHasPermission(value: string | string[] | null | undefined) {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      this.requiredPermissions = [];
    } else {
      this.requiredPermissions = Array.isArray(value) ? value : [value];
    }
    this.updateView();
  }

  @Input()
  set authorityLabsHasPermissionElse(templateRef: TemplateRef<any>) {
    this.elseTemplateRef = templateRef;
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionsService: AppPermissionsService
  ) {}

  ngOnInit(): void {
    this.subscription = this.permissionsService.changes$.subscribe(() => {
      this.updateView();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private updateView(): void {
    // If no permissions required, always show
    const hasPermission =
      this.requiredPermissions.length === 0 ||
      this.permissionsService.hasAny(...this.requiredPermissions);

    if (hasPermission && !this.hasView) {
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasPermission && this.hasView) {
      this.viewContainer.clear();
      if (this.elseTemplateRef) {
        this.viewContainer.createEmbeddedView(this.elseTemplateRef);
      }
      this.hasView = false;
    } else if (!hasPermission && !this.hasView) {
      this.viewContainer.clear();
      if (this.elseTemplateRef) {
        this.viewContainer.createEmbeddedView(this.elseTemplateRef);
      }
    }
  }
}
